import _ from "lodash";

function concat(array, ...items) {
    if (_.isEmpty(array)) {
        return items;
    }
    return _.concat(array, ...items);
}

function getNodeAndAncestors(rootNodes, nodeId, ancestors, idKey = "id") {
    if (_.isEmpty(rootNodes)) {
        return [];
    }
    let newAncestors = ancestors;
    if (ancestors == null) {
        newAncestors = [];
    }
    for (const node of rootNodes) {
        const currentResult = [node, ...newAncestors];
        if (node[idKey] === nodeId) {
            return currentResult;
        }
        const childResult = getNodeAndAncestors(node.children, nodeId, currentResult, idKey);
        if (childResult != null && childResult.length > 0) {
            return childResult;
        }
    }
    return [];
}

function getNode(rootNodes, nodeId, idKey = "id") {
    return getNodeAndAncestors(rootNodes, nodeId, null, idKey)[0];
}

function getFirstLeafAndAncestors(rootNodes, ancestors) {
    const first = _.head(rootNodes);
    if (_.isEmpty(first?.children)) {
        return { leaf: first, ancestors };
    }
    return getFirstLeafAndAncestors(first.children, concat(ancestors, first));
}

function getNodeAndDescendants(nodes, mapper) {
    return _.flatMap(nodes, it => {
        return [
            mapper ? mapper(it) : it,
            ...getNodeAndDescendants(it.children, mapper),
        ];
    });
}

function getNodeAndDescendantIds(nodes) {
    return getNodeAndDescendants(nodes, it => it.id);
}

function fork(rootNodes) {
    if (rootNodes == null) {
        return rootNodes;
    }
    return _.map(rootNodes, node => ({
        ...node,
        children: fork(node.children),
    }));
}

function travelNode(rootNodes, nodeChain, fn) {
    const _travel = (nodes, nc) => {
        const [topNode, ...restNodes] = nc;
        return _.chain(nodes)
            .map(it => {
                if (it === topNode) {
                    if (_.isEmpty(restNodes)) {
                        return fn(topNode);
                    } else {
                        return {
                            ...it,
                            children: _travel(it.children, restNodes),
                        };
                    }
                } else {
                    return it;
                }
            })
            .value();
    };

    const revserChain = _.reverse(nodeChain);

    return _travel(rootNodes, revserChain);
}

function travelChildren(rootNodes, nodeChain, fn) {
    const _travel = (nodes, nc) => {
        if (_.isEmpty(nc)) {
            return fn(nodes);
        }

        const [topNode, ...restNodes] = nc;
        return _.chain(nodes)
            .map(it => {
                if (it === topNode) {
                    return {
                        ...it,
                        children: _travel(it.children, restNodes),
                    };
                } else {
                    return it;
                }
            })
            .value();
    };

    const revserChain = _.reverse(nodeChain);

    return _travel(rootNodes, revserChain);
}

function append(rootNodes, node, targetNodeId) {
    const parentChain = getNodeAndAncestors(rootNodes, targetNodeId);

    return travelNode(rootNodes, parentChain, parent => ({
        ...parent,
        children: concat(parent.children, node),
    }));
}

function _insert(rootNodes, node, targetNodeId, isBefore) {
    const [targetNode, ...parentChain] = getNodeAndAncestors(rootNodes, targetNodeId);
    if (targetNode == null) {
        return;
    }

    return travelChildren(rootNodes, parentChain, targetArray => {
        const targetIndex = _.indexOf(targetArray, targetNode) + (isBefore ? 0 : 1);
        const result = targetArray == null ? [] : [...targetArray];
        result.splice(targetIndex, 0, node);
        return result;
    });
}

function insertBefore(rootNodes, node, targetNodeId) {
    return _insert(rootNodes, node, targetNodeId, true);
}

function insertAfter(rootNodes, node, targetNodeId) {
    return _insert(rootNodes, node, targetNodeId, false);
}

function remove(rootNodes, nodeId) {
    const [node, ...parentChain] = getNodeAndAncestors(rootNodes, nodeId);

    return travelChildren(
        rootNodes,
        parentChain,
        children => _.filter(children, c => c.id !== nodeId),
    );
}

/** 平铺tree 生成没有children的node */
function flatTree(rootNodes) {
    return _.flatMap(rootNodes, node => {
        if (_.isEmpty(node.children)) {
            return node;
        } else {
            return [
                _.omit(node, "children"),
                ...flatTree(node.children),
            ];
        }
    });
}

/** 过滤树中的节点 平铺返回 */
function filterNodes(rootNodes, func, mapper) {
    return _.flatMap(rootNodes, it => {
        const childrenNodes = filterNodes(it.children, func, mapper);
        if (func(it)) {
            return [
                mapper ? mapper(it) : it,
                ...childrenNodes,
            ];
        } else {
            return childrenNodes;
        }
    });
}

function mapTree(rootNodes, nodeMapper) {
    const _mapNodes = nodes => _.map(nodes, it => ({
        ...nodeMapper(it),
        children: _mapNodes(it.children)
    }));
    return _mapNodes(rootNodes);
}

export default {
    getNodeAndAncestors,
    getNode,
    getFirstLeafAndAncestors,
    getNodeAndDescendants,
    getNodeAndDescendantIds,
    fork,
    travelNode,
    travelChildren,
    append,
    insertBefore,
    insertAfter,
    remove,
    flatTree,
    filterNodes,
    mapTree
};
